import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, CalendarDays, MoonStar } from "lucide-react";
import { AlertDialog, Button } from "@akuna/ui";
import { toast } from "@/hooks/use-toast";
import { useOvernightConfig } from "@/hooks/useOvernightConfig";
import type { PanelPet } from "../panel/panelModel";
import { OvernightDialog } from "./OvernightDialog";
import { useRemoveStayOvernight } from "./useStayOvernight";

export interface StayScheduleStripProps {
  pet: PanelPet;
  isoDate: string;
  readOnly?: boolean;
}

/** "2026-07-30" → Date ao meio-dia, para o fuso não empurrar o dia para trás. */
const fromIsoDate = (value: string) => new Date(`${value}T12:00:00`);

/**
 * A primeira coisa do modal: quando o pet entra, quando sai e se dorme aqui.
 * Os horários gravam no blur para não disparar um update por tecla, e o
 * pernoite reaproveita o `OvernightDialog` que o botão de lua do card já abre.
 *
 * As datas são informativas: mudar a data de uma estadia em andamento mexeria em
 * capacidade, comanda e materialização de dias.
 */
export function StayScheduleStrip({ pet, isoDate, readOnly = false }: StayScheduleStripProps) {
  const { i18n, t } = useTranslation("daycareHotel");
  const config = useOvernightConfig();
  const removeOvernight = useRemoveStayOvernight();

  const [overnightOpen, setOvernightOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  // Pernoite é para quem passa o dia e dorme; hotel já dorme no local.
  const showOvernight = !readOnly && Boolean(config.data?.isEnabled) && pet.nature === "daycare";

  const locale = (i18n.resolvedLanguage ?? i18n.language ?? "pt-BR").startsWith("pt") ? "pt-BR" : "en-US";
  const formatDay = (value: string) =>
    new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short" }).format(fromIsoDate(value));

  const confirmRemoveOvernight = async () => {
    try {
      await removeOvernight.mutateAsync({
        reservationId: pet.reservationId,
        isoDate,
        overnightItemName: t("badgeOvernight"),
      });
      setConfirmRemove(false);
      toast({ title: t("panel.overnightRemoved") });
    } catch {
      toast({ variant: "destructive", title: t("panel.overnightRemoveFailed") });
    }
  };

  const showsCheckOut = pet.nature === "hotel" || pet.isOvernight;
  const daySlot = (day: string, label: string, highlight: boolean) => (
    <div
      className={`flex min-w-[8.75rem] items-center gap-2.5 rounded-xl border px-3 py-2 ${
        highlight
          ? "border-akuna-purple/35 bg-purple-50 text-purple-800"
          : "border-border bg-muted/40 text-foreground"
      }`}
    >
      <CalendarDays aria-hidden="true" size={16} className={highlight ? "text-akuna-purple" : "text-muted-foreground"} />
      <div className="min-w-0">
        <p className={`text-xs font-semibold ${highlight ? "text-purple-700" : "text-muted-foreground"}`}>{label}</p>
        <p className="font-display text-sm font-bold leading-tight">{formatDay(day)}</p>
      </div>
    </div>
  );

  return (
    <section className="flex flex-wrap items-center gap-3">
      {daySlot(isoDate, t("panel.scheduleCheckIn"), false)}
      {showsCheckOut ? <ArrowRight aria-hidden="true" size={18} className="text-muted-foreground" /> : null}
      {showsCheckOut ? daySlot(pet.lastStayDate, t("panel.scheduleCheckOut"), pet.nature === "hotel" || pet.isOvernight) : null}

      {showOvernight ? (
        <div className="ml-auto">
          {pet.isOvernight ? (
            <Button
              size="sm"
              variant="outline"
              className="h-10 border-akuna-purple/40 bg-purple-50 text-purple-800 shadow-none hover:border-akuna-purple hover:bg-purple-100"
              disabled={removeOvernight.isPending}
              onClick={() => setConfirmRemove(true)}
            >
              <MoonStar aria-hidden="true" size={16} className="mr-1.5 text-akuna-purple" />
              {t("panel.overnightRemove")}
            </Button>
          ) : (
            <Button size="sm" variant="outline" className="h-10" onClick={() => setOvernightOpen(true)}>
              <MoonStar aria-hidden="true" size={16} className="mr-1.5" />
              {t("panel.overnightAdd")}
            </Button>
          )}
        </div>
      ) : null}

      <OvernightDialog pet={pet} isoDate={isoDate} open={overnightOpen} onOpenChange={setOvernightOpen} />

      <AlertDialog
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        title={t("panel.overnightRemoveTitle")}
        cancelLabel={t("cancel")}
        actionLabel={t("panel.overnightRemove")}
        onAction={() => void confirmRemoveOvernight()}
      >
        {t("panel.overnightRemoveDescription")}
      </AlertDialog>
    </section>
  );
}
